import { AxiosError } from 'axios';
import { JobApiService } from 'jobs/JobApiService';
import { JobUtilsService } from 'jobs/JobUtilsService';
import { JobCategory } from 'jobs/types';
import { Job } from 'jobs/types/Job';
import Cookies from 'js-cookie';
import React, { ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const FeatureJobsContext = React.createContext<{
    jobs: Job[];
    categories: JobCategory[];
    subCategories: JobCategory[];
    catSlug: string;
    subCatSlug: string;
    onSelectSubCatSlug: (slug: string) => void;
    onSelectCatSlug: (slug: string) => void;
    onApplyFilter: (filters: { keyWord?: string; region?: string }) => void;
    loading: boolean;
} | null>(null);

export const FeatureJobsBrowserContextProvider = ({ children }: { children: ReactNode }) => {
    const GUID = localStorage.getItem('GUID');
    const userId = Cookies.get('user_id') || '';
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [jobs, setJobs] = React.useState<Job[]>([]);
    const [categories, setCategories] = React.useState<JobCategory[]>([]);
    const [subCategories, setSubCategories] = React.useState<JobCategory[]>([]);
    const [catSlug, setCategorySlug] = React.useState<string | 'DEFAULT'>(
        params.cat_slug ?? 'DEFAULT'
    );
    const [subCatSlug, setSubCatSlug] = React.useState<string | 'DEFAULT'>(
        params.sub_cat_slug ?? 'DEFAULT'
    );

    const rootPath = location.pathname.split('/')[1];
    const segments = params['*']?.split('/');

    segments?.forEach((s) => {
        console.log(s);
    });

    React.useEffect(() => {
        setLoading(true);
        JobApiService.getJobs({
            guid: GUID ?? '',
            userId: userId,
            seo_slug: location.pathname,
            positionFrom: 0,
            category_slug: params.cat_slug,
            subcategory_slug: params.sub_cat_slug,
            job_city: '',
            keyword: '',
        })
            .then((resp) => {
                if (resp.status === 200) {
                    setJobs(resp.response.jobs);
                    setCategories(resp.response.category);
                }
            })
            .catch((error: AxiosError) => {
                if (error.status === 500) {
                    toast.error(error.message);
                }
            })
            .finally(() => setLoading(false));
    }, [params.cat_slug, params.sub_cat_slug]);

    React.useEffect(() => {
        if (categories.length && catSlug) {
            const jobCat = JobUtilsService.findCategoryBy(categories, 'slug', catSlug);
            if (jobCat) {
                JobApiService.getSubCategories(jobCat.id)
                    .then((resp) => {
                        setSubCategories(resp.response);
                    })
                    .catch((e) => {
                        console.warn(e, 'ERROR');
                    });
            }
        }
    }, [categories, catSlug]);

    const onSelectCatSlug = React.useCallback((slug: string) => {
        setCategorySlug(slug);
        setSubCatSlug('DEFAULT');
        setSubCategories([]);
    }, []);

    const onSelectSubCatSlug = React.useCallback((slug: string) => {
        setSubCatSlug(slug);
    }, []);

    const onApplyFilter = React.useCallback(
        ({ keyWord, region }: { keyWord?: string; region?: string }) => {
            if (!categories.length) return;
            let navigatePath = `/${rootPath}`;

            if (catSlug !== 'DEFAULT') {
                navigatePath += '/c-' + catSlug;
            }

            if (subCatSlug !== 'DEFAULT') {
                navigatePath += '/s-' + subCatSlug;
            }

            if (keyWord) {
                navigatePath += '/k-' + encodeURIComponent(keyWord);
            }

            if (region) {
                navigatePath += '/r-' + encodeURIComponent(region);
            }
            navigate(navigatePath, { replace: true });
        },
        [categories, catSlug, subCatSlug, rootPath]
    );

    const value = React.useMemo(() => {
        return {
            jobs,
            categories,
            subCategories,
            onApplyFilter,
            onSelectCatSlug,
            catSlug,
            subCatSlug,
            onSelectSubCatSlug,
            loading,
        };
    }, [
        jobs,
        categories,
        subCategories,
        onApplyFilter,
        onSelectCatSlug,
        catSlug,
        subCatSlug,
        loading,
        onSelectSubCatSlug,
    ]);

    return <FeatureJobsContext.Provider value={value}>{children}</FeatureJobsContext.Provider>;
};

export const useFeatureJobsBrowserContext = () => {
    const ctx = React.useContext(FeatureJobsContext);
    if (!ctx) {
        throw new Error(
            'useFeatureJobsContext must be used inside FeatureJobsContextProvider'
        );
    }
    return ctx;
};
